import {
  CasLoaderWithBackdrop,
  CasErrorDisplay,
  CasReactHookFormDialog,
} from 'src/compass/shared/components';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import CasEditableDialog from 'src/compass/shared/components/CasTable/CasEditableDialog';
import {
  EntityInfoCMEAccounts,
  EntityInfoLRPAccounts,
  type EntityInfoType,
} from 'src/compass/application/commissions';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import { useUserHasPermission } from 'src/compass/site/routes/useUserPermissions';
import AccountForm from './AccountForm';
import { AccountSchema } from './AccountSchema';

type AccountDialogProps = {
  id: number;
  onClose: VoidFunction;
}

// accountType values returned by Utilities/EntityAccount, and the menu item permission
// that governs the matching commissions dialog.
const COMMISSIONS_INFO: Record<string, { permission: string; entityInfo: EntityInfoType }> = {
  LRP: { permission: 'LRPCOMMISSION', entityInfo: EntityInfoLRPAccounts },
  CME: { permission: 'CMECOMMISSION', entityInfo: EntityInfoCMEAccounts },
};

export default function AccountDialog({ id, onClose }: AccountDialogProps) {
  const commissionsDialog = useCasBoolean();

  const { data, isFetching, isError } = useRetrieveDetailsById({
    entity: `Utilities/EntityAccount`,
    id,
    enabled: !!id,
  });

  const hasLrpCommissions = useUserHasPermission(COMMISSIONS_INFO.LRP.permission);
  const hasCmeCommissions = useUserHasPermission(COMMISSIONS_INFO.CME.permission);

  const mutation = useUpdateMutation({
    url: 'Utilities/EntityAccount',
    onSuccess: (successData) => {
      onSuccessEnqueueSnackbar(successData);
      onClose();
    },
  });

  const { mutate, isPending } = mutation;

  const saveEntity = (entityData: any) => {
    const { entityObj, crmDealObj, ...rest } = entityData;
    mutate({ ...rest, entityId: entityObj.id, crmDealId: crmDealObj.id });
  };

  if (isFetching) return <CasLoaderWithBackdrop />;

  if (isError) return <CasErrorDisplay message="Error retrieving data from the server." />;

  const commissionsInfo = COMMISSIONS_INFO[data?.accountType];
  const hasCommissionsPermission =
    data?.accountType === 'LRP' ? hasLrpCommissions : hasCmeCommissions;
  const showCommissionsLink = !!commissionsInfo && hasCommissionsPermission;

  return (
    <>
      <CasReactHookFormDialog
        open
        onClose={onClose}
        title="Edit Account"
        submitButtonLabel="Update"
        defaultValues={data}
        onSubmit={saveEntity}
        schema={AccountSchema}
      >
        {isPending && <CasLoaderWithBackdrop />}
        <AccountForm
          row={data}
          headerAction={
            showCommissionsLink && (
              <CasLink onClick={commissionsDialog.setTrue}>Commissions</CasLink>
            )
          }
        />
      </CasReactHookFormDialog>
      {commissionsDialog.value && commissionsInfo && (
        <CasEditableDialog
          {...commissionsInfo.entityInfo}
          id={id}
          dialog={commissionsDialog}
          onChanged={commissionsDialog.setFalse}
        />
      )}
    </>
  );
}
